/**
 * ACTION REQUIRED — the things standing between a release and submission.
 *
 * §14 asks for a validation step that lists what must be fixed and blocks submission
 * while a required issue remains. This renders that list; the deciding is done by
 * lib/publisherValidation (Lyrixa's own completeness checks) and, when a backend exists,
 * by LabelGrid's `POST /releases/{release}/validate`.
 *
 * TWO SOURCES, LABELLED SEPARATELY, AND THAT IS THE WHOLE POINT OF THIS COMPONENT.
 * "Lyrixa checks" are ours and they really ran. "Distributor checks" are LabelGrid's and
 * they have NOT run in this build — see the validation step, which says so rather than
 * showing a green tick nobody earned. Folding the two into one list would let a reader
 * believe a distributor approved the release.
 *
 * Blocking issues are red, advisories amber, and each row offers a way to the step that
 * fixes it, because a list of problems with no route to them is a wall.
 */
import { AlertCircle, AlertTriangle, Check } from 'lucide-react';
import type { PublisherIssue } from '../../lib/publisherValidation';
import { c } from '../../styles/theme';

export interface IssueListProps {
  issues: PublisherIssue[];
  /**
   * Jumps to the step that fixes an issue.
   *
   * Optional: the detail page shows the same issues with nowhere to jump to, since the
   * release is no longer being edited in a wizard.
   */
  onGoToStep?: (step: number) => void;
  /** Shown when the list is empty. */
  emptyMessage?: string;
}

export default function IssueList({
  issues,
  onGoToStep,
  emptyMessage = 'Every required field is filled in.',
}: IssueListProps) {
  if (issues.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: `1px solid ${c.border}`,
          background: c.surface2,
          borderRadius: 9,
          padding: '10px 12px',
        }}
      >
        <Check size={14} color={c.success} style={{ flexShrink: 0 }} />
        <p style={{ margin: 0, fontSize: 12, color: c.text2 }}>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 7,
      }}
    >
      {issues.map((issue) => (
        <li
          key={issue.id}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 9,
            /* One border for both severities, with the icon and the "Required"/
               "Recommended" word carrying the difference. A red-bordered row for every
               unfilled field turns the first visit to this step into a wall of alarm,
               when in fact "you have not chosen outlets yet" is the normal state of a
               half-built release. */
            border: `1px solid ${c.border}`,
            background: c.surface2,
            borderRadius: 9,
            padding: '10px 12px',
          }}
        >
          {issue.blocking ? (
            <AlertCircle size={14} color={c.danger} style={{ flexShrink: 0, marginTop: 1 }} />
          ) : (
            <AlertTriangle size={14} color={c.accent} style={{ flexShrink: 0, marginTop: 1 }} />
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: 0, fontSize: 12, color: c.text1, lineHeight: 1.55 }}>
              {issue.message}
            </p>
            {/* The word carries the severity as well as the colour, so the distinction
                survives for a reader who cannot see the difference between the two
                icons. */}
            <p style={{ margin: '3px 0 0', fontSize: 11, color: c.text2 }}>
              {issue.blocking ? 'Required' : 'Recommended'} · Step {issue.step}
            </p>
          </div>
          {onGoToStep && (
            <button
              type="button"
              onClick={() => onGoToStep(issue.step)}
              aria-label={`Go to step ${issue.step} to fix: ${issue.message}`}
              style={{
                flexShrink: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                fontSize: 11.5,
                color: c.accent,
                fontFamily: 'Inter',
              }}
            >
              Fix
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
